import _ from 'lodash';
import $ from 'jquery';
import screenfull from 'screenfull';
import ThumbnailUtil from './ThumbnailUtil';
import ImageConverterWorker from '../workers/ImageConverter.worker';

const debug = require('../debug/debug')('wc:util:DOMUtil');

export function isInRect(rect, x, y) {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

function _togglePageLoadPlaceholder(hide) {
  const el = document.getElementById('page-load-placeholder');
  if (el) {
    el.style.display = hide ? 'none' : '';
  }
}

export function togglePageLoadPlaceholder(hide = false, delay = false) {
  if (delay) {
    return new Promise((resolve) => {
      setTimeout(() => {
        _togglePageLoadPlaceholder(hide);
        resolve();
      });
    });
  } else {
    _togglePageLoadPlaceholder(hide);
  }
}

export function animateScrollIfNeeded(scrollNode: Element, targetNode: Element, config: Object = {}) {
  const rect = targetNode.getBoundingClientRect();
  const parentRect = scrollNode.getBoundingClientRect();
  if (rect.top >= parentRect.top && rect.bottom <= parentRect.bottom) {
    return Promise.resolve();
  }

  return animateScroll(scrollNode, targetNode, config);
}

let _i = 1;
export function animateScroll(scrollNode, targetNode, { dest = 'TOP', speed = 'MEDIUM', axis = 'Y' } = {}) {
  const prefix = `[animateScroll() ${_i++}]`;
  debug(`${prefix} begin. args: `, { scrollNode, targetNode, dest, speed, axis });
  return new Promise((resolve) => {
    let count = 0,
      initialDirectionX,
      initialDirectionY,
      timer,
      fallbackTimer;

    const speeds = {
      FAST: { large: 250, medium: 200, small: 100 },
      MEDIUM: { large: 60, medium: 40, small: 20 },
      SLOW: { large: 30, medium: 15, small: 5 },
      'X-SLOW': { large: 15, medium: 10, small: 5 }
    };

    const done = () => {
      clearInterval(timer);
      clearInterval(fallbackTimer);
      debug(`${prefix} end`);
      return resolve();
    };

    //check if it reach to max possible right or bottom,eg. client.width + scroll.left <= scroll.width
    let isXReach = false; 
    let isYReach = false;
    timer = setInterval(() => {
      count++;
      if (count > 3) {
        if (
          (scrollNode.scrollTop === 0 || scrollNode.scrollTop + scrollNode.clientHeight === scrollNode.scrollHeight) &&
          (scrollNode.scrollLeft === 0 || scrollNode.scrollLeft + scrollNode.clientWidth === scrollNode.scrollWidth)
        ) {
          debug(`${prefix} reached the end`);
          return done();
        }
      }

      const scrollNodeRect = scrollNode.getBoundingClientRect();
      const targetNodeRect = targetNode.getBoundingClientRect();
      const targetX =
        dest === 'CENTER'
          ? scrollNodeRect.left + scrollNodeRect.width / 2
          : dest === 'LEFT'
            ? scrollNodeRect.left + 50
            : scrollNodeRect.right - 50;

      const targetY =
        dest === 'CENTER'
          ? scrollNodeRect.top + scrollNodeRect.height / 2
          : dest === 'TOP'
            ? scrollNodeRect.top + 50
            : scrollNodeRect.bottom - 50;

      const currentX = targetNodeRect.left;
      const diffX = Math.abs(targetX - currentX);
      const directionX = currentX > targetX ? 'RIGHT' : 'LEFT';

      const currentY = targetNodeRect.top;
      const diffY = Math.abs(targetY - currentY);
      const directionY = currentY > targetY ? 'DOWN' : 'UP';

      if (!initialDirectionX) {
        initialDirectionX = directionX;
        debug(`${prefix} initialDirectionX: `, initialDirectionX);
      }

      if (!initialDirectionY) {
        initialDirectionY = directionY;
        debug(`${prefix} initialDirectionY: `, initialDirectionY);
      }

      let stepX;
      if (diffX > 350) {
        stepX = speeds[speed].large;
      } else if (diffX > 200) {
        stepX = speeds[speed].medium;
      } else {
        stepX = speeds[speed].small;
      }

      let stepY;
      if (diffY > 350) {
        stepY = speeds[speed].large;
      } else if (diffY > 200) {
        stepY = speeds[speed].medium;
      } else {
        stepY = speeds[speed].small;
      }

      if (axis === 'Y' || axis === 'BOTH') {
        if (diffY < 10 || directionY !== initialDirectionY) {
          if (axis === 'Y') {
            debug(`${prefix} done with y`);
            return done();
          } else if (diffX < 10 || directionX !== initialDirectionX) {
            debug(`${prefix} done with x,y`);
            return done();
          }
        }

        const scrollTop = currentY > targetY ? stepY : -stepY;
        debug(`${prefix} scrollTop change: `, scrollTop);
        const prevScrollTop = scrollNode.scrollTop;
        if (diffY > 10) {
          scrollNode.scrollTop += scrollTop;
        } else {
          isYReach = true
        }
        if (prevScrollTop === scrollNode.scrollTop && isXReach) {
            done();
        }
      }

      if (axis === 'X' || axis === 'BOTH') {
        if (diffX < 10 || directionX !== initialDirectionX) {
          if (axis === 'X') {
            debug(`${prefix} done with x`);
            return done();
          } else if (diffY < 10 || directionY !== initialDirectionY) {
            debug(`${prefix} done with x,y`);
            return done();
          }
        }

        const scrollLeft = currentX > targetX ? stepX : -stepX;
        debug(`${prefix} scrollLeft change: `, scrollLeft);
        const prevScrollLeft = scrollNode.scrollLeft; 
        if (diffX > 10) {
          scrollNode.scrollLeft += scrollLeft;
        } else {
          isXReach = true;
        }
        if (prevScrollLeft === scrollNode.scrollLeft && isYReach) {
          done()
        }
      }
    }, 20);

    // Give up after 5 seconds.
    fallbackTimer = setTimeout(() => {
      debug(`${prefix} fallback timer forcing end`);
      done();
    }, 5000);
  });
}

/**
 * Converts an array of image urls into data urls.
 * @param {String[]} urls - The image urls.
 * @return {Promise.<String[], error>} Promise that resolves with the data urls.
 */
export function convertImagesToDataUrls(urls) {
  const imageConverter = new ImageConverterWorker();
  const start = Date.now();
  let done = false;

  const promise = new Promise((resolve, reject) => {
    debug(`[convertImagesToDataUrls()] Begin. Processing ${urls.length} images`);

    imageConverter.onmessage = (e) => {
      const { type, data } = e.data;

      if (type === 'CONVERT_IMAGES_ERROR') {
        const err = new Error(data);
        debug('[convertImagesToDataUrls()] error: ', err);
        done = true;
        imageConverter.terminate();
        return reject(err);
      }

      const end = Date.now();
      debug(`[convertImagesToDataUrls()] End. Elapsed time: ${end - start}ms`);
      done = true;
      imageConverter.terminate();
      resolve(data);
    };
  });

  const cancel = () => {
    if (!done) {
      const end = Date.now();
      debug(`[convertImagesToDataUrls()] Canceled. Elapsed time: ${end - start}ms`);
      imageConverter.terminate();
    }
  };

  imageConverter.postMessage({
    type: 'CONVERT_IMAGES',
    data: urls
  });

  return {
    promise,
    cancel
  };
}

export function newId() {
  const min = 1000000;
  const max = 10000000;
  return (Math.floor(Math.random() * (max - min)) + min).toString(16);
}

export async function computeMosaic(assets, options = {}) {
  debug('[computeMosaic()] begin');
  const start = Date.now();

  const {
    previousRows = null,
    previousTargetRowHeight = null,
    scale = 1,
    targetRowHeight = 275,
    maxRowWidth = 1200,
    leftOffset = 0,
    topOffset = 0,
    useThumbnail = true,
    useCache = true
  } = options;

  if (!(assets && assets.length)) {
    debug('[computeMosaic()] no assets. return.');
    return null;
  }

  let shouldRecompute = true;

  if (previousRows) {
    let storedAssets = _.map(_.flatten(previousRows), (element) => element.asset);
    let arraysEqual = assets.length === storedAssets.length;
    if (arraysEqual) {
      _.each(storedAssets, (asset, index) => {
        if (asset != assets[index]) arraysEqual = false;
      });
    }

    shouldRecompute = !arraysEqual;
  }

  const newTargetRowHeight = targetRowHeight * scale; // The desired row height. Actual row height will not be exactly this, and may even drastically differ
  if (previousTargetRowHeight != newTargetRowHeight) {
    shouldRecompute = true;
  }

  if (!shouldRecompute) {
    debug('[computeMosaic()] !shouldRecompute. return.');
    return null;
  }

  const imgs = await downloadMosaicImages(assets, useThumbnail, useCache);
  const rowTolerance = 50; // Amount, in pixels, that the intial row width can underflow before being scaled up

  let rows = [[]];
  let rowHeights = [];

  function getRowWidth(row) {
    return _.reduce(
      row,
      (total, element) => {
        return total + element.width;
      },
      0
    );
  }

  // calcutes dimensions for each image, splits into rows, and puts result in (rows)
  assets.filter((asset) => !!imgs[asset.getFuid()]).forEach((asset) => {
    let rowElement = { asset: asset, image: imgs[asset.getFuid()] }; // has image, width/height, and asset properties

    if (maxRowWidth - getRowWidth(rows[rows.length - 1]) < rowTolerance) {
      const rowScaleFactor = maxRowWidth / getRowWidth(rows[rows.length - 1]);
      for (let i = 0; i < rows[rows.length - 1].length; i++) {
        rows[rows.length - 1][i].width *= rowScaleFactor;
        rows[rows.length - 1][i].height *= rowScaleFactor;
      }
      rows.push([]);
    }

    const naturalWidth = rowElement.image.naturalWidth || 1;
    const naturalHeight = rowElement.image.naturalHeight || 1;
    const scaleFactor = newTargetRowHeight / naturalHeight;
    rowElement.height = newTargetRowHeight;
    rowElement.width = scaleFactor * naturalWidth;
    rows[rows.length - 1].push(rowElement);
  });

  // last row doesnt trigger the row resizing because there's no next asset
  // but only resize if it's too wide
  if (getRowWidth(rows[rows.length - 1]) > maxRowWidth) {
    const rowScaleFactor = maxRowWidth / getRowWidth(rows[rows.length - 1]);
    for (let i = 0; i < rows[rows.length - 1].length; i++) {
      rows[rows.length - 1][i].width *= rowScaleFactor;
      rows[rows.length - 1][i].height *= rowScaleFactor;
    }
  }

  // calculate/store positions
  let top = topOffset;
  _.each(rows, (row) => {
    let left = leftOffset;
    _.each(row, (element) => {
      element.top = top;
      element.left = left;

      left += element.width;
    });

    const rowHeight = row[0].height;
    top += rowHeight;
    rowHeights.push(rowHeight);
  });

  const time = Date.now() - start;
  debug('[computeMosaic()] end. Elapsed ms: ', time);

  return {
    rows: _.flatten(rows),
    rowCount: rows.length,
    targetRowHeight: newTargetRowHeight,
    totalHeight: rowHeights.reduce((sum, rowHeight) => sum + rowHeight, 0)
  };
}

async function downloadMosaicImages(assets, useThumbnail, useCache) {
  let thumbnails;
  if (useCache) {
    if (!window.gs.thumbnails) {
      window.gs.thumbnails = {};
    }
    thumbnails = window.gs.thumbnails;
  } else {
    thumbnails = {};
  }

  const promises = assets.map(
    (asset) =>
      new Promise((resolve) => {
        if (useThumbnail && asset.getFuid() in thumbnails) {
          return resolve();
        }

        let img = new Image();
        img.alt = 'No preview available';
        img.src = useThumbnail ? ThumbnailUtil.toThumbUrl(asset) : ThumbnailUtil.createImagePreviewUrl(asset);

        img.onload = img.onerror = () => {
          thumbnails[asset.getFuid()] = img;
          resolve();
        };
      })
  );

  await Promise.all(promises);
  return thumbnails;
}

export function enableRootNodeScroll(enable, yOnly = true, reset = true) {
  const value = enable ? 'auto' : 'hidden';
  const node = document.getElementById('root');
  if (!node) {
    return;
  }

  const attr = yOnly ? 'overflowY' : 'overflow';
  node.style[attr] = value;

  if (!enable && reset) {
    node.scrollTop = 0;
    if (!yOnly) {
      node.scrollLeft = 0;
    }
  }
}

export function calculateOverflow(container, element) {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  return {
    x: elementRect.x + elementRect.width - (containerRect.x + containerRect.width),
    y: elementRect.y + elementRect.height - (containerRect.y + containerRect.height)
  };
}

export function showNativeUploadDialog() {
  $('#upload').trigger('click');
}

export function canRootNodeScroll() {
  const node = document.getElementById('root');
  return node ? node.style.overflow !== 'hidden' : null;
}

export function copyToClipboard(text) {
  const textarea = document.createElement('textarea');
  textarea.style.position = 'fixed';
  textarea.style.left = '-1000px';
  document.body.appendChild(textarea);
  textarea.textContent = text;
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

export function copyRichTextToClipboard(richText, plainTextFallback) {
  const listener = (event: ClipboardEvent) => {
    event.preventDefault();
    event.clipboardData.setData('text/html', richText);
    event.clipboardData.setData('text/plain', plainTextFallback);
  };
  document.addEventListener('copy', listener);
  document.execCommand('copy');
  document.removeEventListener('copy', listener);
}

export function scrollToTop() {
  const node = document.getElementById('root');
  if (node) {
    node.scrollTop = 0;
  }
}

export function getWidthInsideScrollbar() {
  const node = document.querySelector('[name="LoggedBlock"]');
  return node ? node.getBoundingClientRect().width : window.innerWidth;
}

export function injectIframe(url) {
  debug('[injectIframe()] url: ', url);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.top = '-1000px';
  iframe.style.left = '-1000px';
  iframe.style.width = '10px';
  iframe.style.height = '10px';
  iframe.style.overflow = 'hidden';
  iframe.src = url;
  document.body.appendChild(iframe);
}

export function toggleFullscreenClass() {
  if (screenfull.isFullscreen) {
    $('body').addClass('fullscreen');
  } else {
    $('body').removeClass('fullscreen');
  }
}

export function isFullscreen() {
  return $('body').hasClass('fullscreen');
}

/**
 * Returns a DOM node from an argument `x` of any type. If `x` is a
 * function, assumes it is a DOM node getter. If `x` is an HTMLElement,
 * returns `x`. If `x` is a string, assumes `x` is a selector to pass
 * to `document.querySelector()`.
 */
export function getDomNodeFromAnyTypeArg(x: any) {
  if (!x) {
    return null;
  }
  if (typeof x === 'function') {
    return x();
  }
  if (typeof x === 'string') {
    return document.querySelector(x);
  }
  if (x instanceof HTMLElement) {
    return x;
  }
  if (typeof x === 'object' && x.nodeType > 0 && typeof x.ownerDocument === 'object') {
    return x;
  }

  return null;
}
